import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const CERTIFICATE_PAGE_WIDTH_PX = 899;
const CERTIFICATE_PAGE_HEIGHT_PX = 1150;
const PDF_DPI = 96;
const MM_PER_INCH = 25.4;
const pxToMm = (px) => (px / PDF_DPI) * MM_PER_INCH;

// Convert oklch color to RGB using canvas
const oklchToRgb = (oklchValue) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = oklchValue;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return a < 255 ? `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})` : `rgb(${r}, ${g}, ${b})`;
};

// Convert oklch colors in a cloned document for html2canvas compatibility
const convertOklchInClonedDocument = (clonedDoc) => {
  // Convert oklch in all stylesheets
  Array.from(clonedDoc.styleSheets).forEach((sheet) => {
    try {
      Array.from(sheet.cssRules || []).forEach((rule) => {
        if (rule.style) {
          const cssText = rule.cssText;
          if (cssText.includes('oklch')) {
            // Replace oklch values in the rule
            const oklchRegex = /oklch\([^)]+\)/g;
            const matches = cssText.match(oklchRegex);
            if (matches) {
              matches.forEach((match) => {
                const rgbValue = oklchToRgb(match);
                rule.style.cssText = rule.style.cssText.replace(match, rgbValue);
              });
            }
          }
        }
      });
    } catch (e) {
      // Cross-origin stylesheets will throw, ignore them
    }
  });

  // Convert oklch in inline styles and computed styles
  const allElements = clonedDoc.querySelectorAll('*');
  allElements.forEach((el) => {
    const computedStyle = clonedDoc.defaultView.getComputedStyle(el);
    const colorProps = [
      'color',
      'background-color',
      'border-color',
      'border-top-color',
      'border-right-color',
      'border-bottom-color',
      'border-left-color',
      'outline-color',
    ];

    colorProps.forEach((prop) => {
      const value = computedStyle.getPropertyValue(prop);
      if (value && value.includes('oklch')) {
        const camelProp = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        el.style[camelProp] = oklchToRgb(value);
      }
    });

    // Handle box-shadow separately as it can have multiple values
    const boxShadow = computedStyle.getPropertyValue('box-shadow');
    if (boxShadow && boxShadow.includes('oklch')) {
      el.style.boxShadow = boxShadow.replace(/oklch\([^)]+\)/g, (match) => oklchToRgb(match));
    }
  });
};

export const waitForNextPaint = async () => {
  await new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

  await new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
};

const addCanvasToPdf = (pdf, canvas) => {
  const imgData = canvas.toDataURL('image/png');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const paddingHorizontal = pxToMm(10);
  const usableWidth = Math.max(pageWidth - 2 * paddingHorizontal, 0);
  const scale = Math.min(usableWidth / canvas.width, pageHeight / canvas.height);

  const imgScaledWidth = canvas.width * scale;
  const imgScaledHeight = canvas.height * scale;

  const offsetX = (pageWidth - imgScaledWidth) / 2;
  const offsetY = (pageHeight - imgScaledHeight) / 2;

  pdf.addImage(imgData, 'PNG', offsetX, offsetY, imgScaledWidth, imgScaledHeight);
};

const createCertificateCanvas = async (content, { ignoreElements } = {}) => {
  if (!content) {
    return null;
  }

  return html2canvas(content, {
    useCORS: true,
    scale: 2,
    ignoreElements,
    onclone: (clonedDoc) => {
      // Convert oklch colors to RGB in the cloned document for html2canvas compatibility
      convertOklchInClonedDocument(clonedDoc);
    },
  });
};

export const exportCertificatePdf = async ({
  frontElement,
  backElement,
  fileName,
  ignoreElements,
}) => {
  if (!frontElement || !backElement) {
    return;
  }

  const [frontCanvas, backCanvas] = await Promise.all([
    createCertificateCanvas(frontElement, { ignoreElements }),
    createCertificateCanvas(backElement, { ignoreElements }),
  ]);

  if (!frontCanvas || !backCanvas) {
    return;
  }

  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: [pxToMm(CERTIFICATE_PAGE_WIDTH_PX), pxToMm(CERTIFICATE_PAGE_HEIGHT_PX)],
  });

  addCanvasToPdf(pdf, frontCanvas);
  pdf.addPage([pxToMm(CERTIFICATE_PAGE_WIDTH_PX), pxToMm(CERTIFICATE_PAGE_HEIGHT_PX)], 'p');
  addCanvasToPdf(pdf, backCanvas);

  const pdfBlob = pdf.output('blob');
  saveAs(pdfBlob, fileName);
};
